import React, { useEffect, useState } from 'react';
import { ArrowsUpDownIcon } from '@heroicons/react/24/outline';
import { BottomSheet } from '../ui/BottomSheet';

export type CobrosFilterMode = 'pendiente' | 'facturas' | 'pagos';

export interface CobrosFiltersSheetProps {
  open: boolean;
  onClose: () => void;
  mode: CobrosFilterMode;
  status: string;
  onStatusChange: (v: string) => void;
  startDate: string;
  onStartDateChange: (v: string) => void;
  endDate: string;
  onEndDateChange: (v: string) => void;
  paymentMethod?: string;
  onPaymentMethodChange?: (v: string) => void;
  paymentMethods?: { value: string; label: string }[];
  statusOptions: { value: string; label: string }[];
  sortDir: 'asc' | 'desc';
  onSortDirChange: (d: 'asc' | 'desc') => void;
  onClear: () => void;
  clientLabel?: string;
}

const selectCls =
  'h-11 w-full rounded-xl border border-[#E5EAF1] bg-white px-3 text-sm font-medium text-slate-800 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200';

export const CobrosFiltersSheet: React.FC<CobrosFiltersSheetProps> = ({
  open,
  onClose,
  mode,
  status,
  onStatusChange,
  startDate,
  onStartDateChange,
  endDate,
  onEndDateChange,
  paymentMethod = '',
  onPaymentMethodChange,
  paymentMethods = [],
  statusOptions,
  sortDir,
  onSortDirChange,
  onClear,
  clientLabel = 'Todos',
}) => {
  const [draft, setDraft] = useState({
    status,
    startDate,
    endDate,
    paymentMethod,
    sortDir,
  });

  useEffect(() => {
    if (open) {
      setDraft({ status, startDate, endDate, paymentMethod, sortDir });
    }
  }, [open, status, startDate, endDate, paymentMethod, sortDir]);

  const apply = () => {
    onStatusChange(draft.status);
    onStartDateChange(draft.startDate);
    onEndDateChange(draft.endDate);
    onPaymentMethodChange?.(draft.paymentMethod);
    onSortDirChange(draft.sortDir);
    onClose();
  };

  const clear = () => {
    setDraft({
      status: '',
      startDate: '',
      endDate: '',
      paymentMethod: '',
      sortDir: 'asc',
    });
    onClear();
    onClose();
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Filtros"
      footer={
        <div className="space-y-2 pb-1">
          <button
            type="button"
            onClick={apply}
            className="flex h-12 w-full items-center justify-center rounded-xl bg-primary-600 text-sm font-semibold text-white shadow-sm transition hover:bg-primary-700 active:scale-[0.99]"
          >
            Aplicar filtros
          </button>
          <button
            type="button"
            onClick={clear}
            className="flex h-10 w-full items-center justify-center text-sm font-medium text-slate-500 transition hover:text-slate-800"
          >
            Limpiar filtros
          </button>
        </div>
      }
    >
      <div className="space-y-4 py-1">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Estado</span>
          <select
            className={selectCls}
            value={draft.status}
            onChange={(e) => setDraft((d) => ({ ...d, status: e.target.value }))}
          >
            {statusOptions.map((o) => (
              <option key={o.value || 'all'} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-600">Desde</span>
            <input
              type="date"
              className={selectCls}
              value={draft.startDate}
              onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-600">Hasta</span>
            <input
              type="date"
              className={selectCls}
              value={draft.endDate}
              onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))}
            />
          </label>
        </div>

        {mode === 'pagos' && onPaymentMethodChange ? (
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-slate-600">Método</span>
            <select
              className={selectCls}
              value={draft.paymentMethod}
              onChange={(e) => setDraft((d) => ({ ...d, paymentMethod: e.target.value }))}
            >
              <option value="">Todos</option>
              {paymentMethods.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Cliente</span>
          <div className="flex h-11 items-center rounded-xl border border-[#E5EAF1] bg-slate-50 px-3 text-sm text-slate-600">
            {clientLabel}
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-slate-600">Ordenar</span>
          <button
            type="button"
            onClick={() =>
              setDraft((d) => ({ ...d, sortDir: d.sortDir === 'asc' ? 'desc' : 'asc' }))
            }
            className="inline-flex h-11 items-center justify-between rounded-xl border border-[#E5EAF1] bg-white px-3 text-sm font-medium text-slate-700"
          >
            <span>{draft.sortDir === 'asc' ? 'Fecha ascendente' : 'Fecha descendente'}</span>
            <ArrowsUpDownIcon className="h-5 w-5 text-slate-400" />
          </button>
        </div>
      </div>
    </BottomSheet>
  );
};

export default CobrosFiltersSheet;
